import React, { useMemo, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const TITLES = ['Mr.', 'Miss', 'Mrs.', 'Ms.', 'Dr.', 'Prof.', 'Rev.', 'Eng.', 'Hon.'];

const OTP_CSRF = 'mevhbteacv4eLKGhZ7HfaCCDFeB3l1I5U7quP4EhtfGYsu6K8K9ljvimnFOQtaBw';

const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan',
  'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi',
  'Cabo Verde', 'Cambodia', 'Cameroon', 'Canada', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo (Congo-Brazzaville)', 'Costa Rica', 'Côte d’Ivoire', 'Croatia', 'Cuba', 'Cyprus', 'Czechia',
  'Democratic Republic of the Congo', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic',
  'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia',
  'Fiji', 'Finland', 'France',
  'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana',
  'Haiti', 'Honduras', 'Hungary',
  'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy',
  'Jamaica', 'Japan', 'Jordan',
  'Kazakhstan', 'Kenya', 'Kiribati', 'Kuwait', 'Kyrgyzstan',
  'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg',
  'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar',
  'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway',
  'Oman',
  'Pakistan', 'Palau', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal',
  'Qatar',
  'Romania', 'Russia', 'Rwanda',
  'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'São Tomé and Príncipe', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria',
  'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu',
  'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan',
  'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam',
  'Yemen',
  'Zambia', 'Zimbabwe'
];

// --- ANIMATION COMPONENTS ---

const FloatingParticle = ({ delay, duration, x, y, size }) => (
  <motion.div
    className="absolute rounded-full bg-white/20 backdrop-blur-sm"
    style={{
      width: size,
      height: size,
      left: x,
      top: y
    }}
    animate={{
      y: [0, -20, 0],
      x: [0, 10, 0],
      opacity: [0.3, 0.6, 0.3],
    }}
    transition={{
      duration: duration,
      repeat: Infinity,
      ease: "easeInOut",
      delay: delay,
    }}
  />
);

const AntiGravityBackground = () => {
  // Generate random particles
  const particles = useMemo(() => {
    return [...Array(15)].map((_, i) => ({
      id: i,
      size: Math.random() * 60 + 20, // 20px to 80px
      x: `${Math.random() * 100}%`,
      y: `${Math.random() * 100}%`,
      duration: Math.random() * 5 + 5, // 5s to 10s
      delay: Math.random() * 2,
    }));
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <FloatingParticle
          key={p.id}
          {...p}
        />
      ))}
    </div>
  );
};

// --- MAIN COMPONENT ---

// --- UI HELPERS ---
const InputField = ({ id, label, error, type = 'text', ...props }) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === 'password';
  const inputType = isPassword ? (showPassword ? 'text' : 'password') : type;

  return (
    <div className="w-full">
      <label htmlFor={id} className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1.5">{label}</label>
      <div className="relative">
        <input
          id={id}
          type={inputType}
          className={`w-full bg-transparent border-b ${error ? 'border-red-400' : 'border-gray-300'} py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-black focus:outline-none transition-colors ${isPassword ? 'pr-8' : ''}`}
          {...props}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-0 top-2 text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            {showPassword ? (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            )}
          </button>
        )}
      </div>
      {error && <p className="mt-1 text-[10px] text-red-500">{error}</p>}
    </div>
  );
};

const SelectField = ({ id, label, error, children, ...props }) => (
  <div className="w-full">
    <label htmlFor={id} className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1.5">{label}</label>
    <div className="relative">
      <select
        id={id}
        className={`w-full bg-transparent border-b ${error ? 'border-red-400' : 'border-gray-300'} py-2 text-sm text-gray-900 focus:border-black focus:outline-none appearance-none transition-colors`}
        {...props}
      >
        {children}
      </select>
      <span className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-xs">▼</span>
    </div>
    {error && <p className="mt-1 text-[10px] text-red-500">{error}</p>}
  </div>
);

// --- MAIN COMPONENT ---

export default function SignUp() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    email: '',
    country: 'Ghana',
    city: '',
    gender: '',
    username: '',
    password: '',
    confirm_password: '',
  });

  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [apiErrors, setApiErrors] = useState({});

  // OTP state
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpSecondsLeft, setOtpSecondsLeft] = useState(0);
  const [otpNotice, setOtpNotice] = useState({ type: '', text: '' });

  const OTP_TTL_SECONDS = 300;

  // --- OTP LOGIC ---
  const startOtpCountdown = () => setOtpSecondsLeft(OTP_TTL_SECONDS);

  useEffect(() => {
    if (!otpOpen || otpSecondsLeft <= 0) return;
    const id = setInterval(() => setOtpSecondsLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [otpOpen, otpSecondsLeft]);

  const formatMMSS = (secs) => {
    const m = String(Math.floor(secs / 60)).padStart(2, '0');
    const s = String(secs % 60).padStart(2, '0');
    return `${m}:${s}`;
  };

  const onChange = (key) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
    // Clear API error for this field when user types
    if (apiErrors[key]) {
      setApiErrors(prev => ({ ...prev, [key]: '' }));
    }
  };

  const onBlur = (key) => () => {
    setTouched((prev) => ({ ...prev, [key]: true }));
  };

  const errors = useMemo(() => {
    const e = {};
    if (!form.title) e.title = 'Required';
    if (!form.first_name.trim()) e.first_name = 'Required';
    if (!form.last_name.trim()) e.last_name = 'Required';

    if (!form.phone_number.trim()) {
      e.phone_number = 'Required';
    } else if (form.phone_number.replace(/\D/g, '').length < 9) {
      e.phone_number = 'Invalid number';
    }

    if (!form.country) e.country = 'Required';
    if (!form.city.trim()) e.city = 'Required';
    if (!form.gender) e.gender = 'Required';
    if (!form.username.trim()) e.username = 'Required';

    if (!form.password) {
      e.password = 'Required';
    } else if (form.password.length < 8) {
      e.password = 'Min 8 chars';
    }

    if (!form.confirm_password) {
      e.confirm_password = 'Confirm password';
    } else if (form.confirm_password !== form.password) {
      e.confirm_password = 'Passwords do not match';
    }

    // Email is optional, but if provided, must be valid
    if (form.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
      e.email = 'Invalid email';
    }

    return e;
  }, [form]);

  const showErr = (field) => (submitted || touched[field]) && (errors[field] || apiErrors[field]);

  const checkExistence = async () => {
    const newApiErrors = {};
    let hasError = false;

    try {
      // Check Username
      const userRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/appointment-users/username-exist/?username=${form.username.trim()}`);
      const userData = await userRes.json().catch(() => ({}));
      if (userData.exists) {
        newApiErrors.username = 'Username already exists';
        hasError = true;
      }

      // Check Phone
      const phoneRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/appointment-users/phone-number-exist/?phone=${form.phone_number.trim()}`);
      const phoneData = await phoneRes.json().catch(() => ({}));
      if (phoneData.exists) {
        newApiErrors.phone_number = 'Phone number already exists';
        hasError = true;
      }

      // Check Email (if provided)
      if (form.email.trim()) {
        const emailRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/appointment-users/email-exist/?email=${encodeURIComponent(form.email.trim())}`);
        const emailData = await emailRes.json().catch(() => ({}));
        if (emailData.exists) {
          newApiErrors.email = 'Email already exists';
          hasError = true;
        }
      }

    } catch (e) {
      console.error("Validation check failed", e);
      // Optionally handle network error during validation, but for now we might proceed or show generic error
    }

    setApiErrors(newApiErrors);
    return !hasError;
  };

  const sendOtp = async () => {
    const raw = form.phone_number.trim();
    if (!raw) {
      setServerError('Phone number is required for verification');
      return;
    }
    setOtpLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/otp/send/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRFTOKEN': OTP_CSRF },
        body: JSON.stringify({ phone_number: raw, purpose: 'general' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.status === false) throw new Error(data?.message || 'Failed to send OTP');

      setOtpDigits(['', '', '', '', '', '']);
      setOtpOpen(true);
      setOtpNotice({ type: '', text: '' });
      startOtpCountdown();
    } catch (err) {
      setServerError(String(err?.message || err));
    } finally {
      setOtpLoading(false);
    }
  };

  const createAccount = async () => {
    setLoading(true);
    try {
      const { confirm_password, ...rest } = form;
      const payload = {
        ...rest,
        role: 'CUSTOMER',
      };

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/appointment-users/`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRFTOKEN': OTP_CSRF,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg = data?.message || data?.detail || 'Failed to create account';
        throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
      }

      // Save tokens and user data
      if (data.access) localStorage.setItem('emba_access', data.access);
      if (data.refresh) localStorage.setItem('emba_refresh', data.refresh);
      localStorage.setItem('emba_user', JSON.stringify(data.user || data));

      setSuccessMessage('Account created successfully. Redirecting...');
      setOtpOpen(false); // Close modal if open
      setTimeout(() => {
        navigate('/my-bookings');
      }, 1000);
    } catch (err) {
      setServerError(String(err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    const code = otpDigits.join('');
    if (code.length !== 6) {
      setOtpNotice({ type: 'error', text: 'Enter 6-digit code' });
      return;
    }
    setOtpLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/otp/verify/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRFTOKEN': OTP_CSRF },
        body: JSON.stringify({ phone_number: form.phone_number.trim(), purpose: 'general', code }),
      });
      const data = await res.json().catch(() => ({}));
      const verified = res.ok && (data?.is_verified === true || data?.status === true);

      if (!verified) {
        const msg = data?.message || data?.detail || 'Invalid OTP';
        setOtpNotice({ type: 'error', text: msg });
        throw new Error(msg);
      }

      setOtpNotice({ type: 'success', text: 'Verified. Creating account...' });
      await createAccount();
    } catch (err) {
      // Error handled in UI via otpNotice or serverError if needed
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setServerError('');
    setSuccessMessage('');
    setApiErrors({}); // Clear previous API errors

    if (Object.keys(errors).length > 0) {
      return;
    }

    setLoading(true); // Show loading while checking existence
    try {
      const canProceed = await checkExistence();
      if (!canProceed) {
        setLoading(false);
        return;
      }
      setLoading(false);
      // Trigger OTP flow instead of direct creation
      await sendOtp();
    } catch (e) {
      setLoading(false);
      setServerError('Validation failed. Please try again.');
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-white">

      {/* LEFT PANEL - VISUAL */}
      <div className="relative w-full md:w-5/12 lg:w-1/2 h-64 md:h-auto overflow-hidden bg-black">
        <img
          src="https://skyphaseobeng.s3.eu-north-1.amazonaws.com/bridal4.jpg"
          alt="Background"
          className="absolute inset-0 w-full h-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/60" />

        {/* Anti-Gravity Animation */}
        <AntiGravityBackground />

        <div className="absolute inset-0 flex flex-col justify-between p-8 md:p-12 text-white z-10">
          <div>
            <Link to="/" className="text-xs tracking-[0.4em] font-bold hover:opacity-80 transition-opacity">
              EMBA BY EMEFA
            </Link>
          </div>
          <div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="text-4xl md:text-5xl lg:text-6xl font-serif tracking-tight mb-4"
            >
              Join the <br /> Experience.
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="text-sm tracking-wider opacity-80 max-w-xs font-light"
            >
              Create an account to manage your fittings, view exclusive collections, and book appointments.
            </motion.p>
          </div>
        </div>
      </div>

      {/* RIGHT PANEL - FORM */}
      <div className="relative z-20 w-full md:w-7/12 lg:w-1/2 flex items-center justify-center p-6 md:p-12 lg:p-20 overflow-y-auto bg-white">
        <div className="w-full max-w-lg">
          <div className="mb-10">
            <h1 className="text-2xl font-serif text-gray-900 mb-2">Create Account</h1>
            <p className="text-sm text-gray-500">Enter your details to get started.</p>
          </div>

          {/* Alerts */}
          {serverError && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mb-6 p-3 bg-red-50 text-red-600 text-xs tracking-wide border-l-2 border-red-500">
              {serverError}
            </motion.div>
          )}
          {successMessage && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mb-6 p-3 bg-green-50 text-green-600 text-xs tracking-wide border-l-2 border-green-500">
              {successMessage}
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">

            {/* Personal Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <SelectField
                id="title"
                label="Title"
                value={form.title}
                onChange={onChange('title')}
                onBlur={onBlur('title')}
                error={showErr('title')}
              >
                <option value="">-</option>
                {TITLES.map(t => <option key={t} value={t}>{t}</option>)}
              </SelectField>

              <div className="md:col-span-2 grid grid-cols-2 gap-4">
                <InputField
                  id="firstName"
                  label="First Name"
                  placeholder="Ama"
                  value={form.first_name}
                  onChange={onChange('first_name')}
                  onBlur={onBlur('first_name')}
                  error={showErr('first_name')}
                />
                <InputField
                  id="lastName"
                  label="Last Name"
                  placeholder="Mensah"
                  value={form.last_name}
                  onChange={onChange('last_name')}
                  onBlur={onBlur('last_name')}
                  error={showErr('last_name')}
                />
              </div>
            </div>

            {/* Contact */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                id="phone"
                label="Phone"
                type="tel"
                placeholder="0242123456"
                value={form.phone_number}
                onChange={onChange('phone_number')}
                onBlur={onBlur('phone_number')}
                error={showErr('phone_number')}
              />
              <InputField
                id="email"
                label="Email (Optional)"
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={onChange('email')}
                onBlur={onBlur('email')}
                error={showErr('email')}
              />
            </div>

            {/* Location */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <SelectField
                id="country"
                label="Country"
                value={form.country}
                onChange={onChange('country')}
                onBlur={onBlur('country')}
                error={showErr('country')}
              >
                <option value="">Select</option>
                {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
              </SelectField>
              <InputField
                id="city"
                label="City"
                placeholder="Accra"
                value={form.city}
                onChange={onChange('city')}
                onBlur={onBlur('city')}
                error={showErr('city')}
              />
            </div>

            {/* Account */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                id="username"
                label="Username"
                placeholder="username"
                value={form.username}
                onChange={onChange('username')}
                onBlur={onBlur('username')}
                error={showErr('username')}
              />

              <div>
                <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-2">Gender</label>
                <div className="flex gap-2">
                  {['M', 'F', 'O'].map((g) => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, gender: g }))}
                      className={`w-8 h-8 rounded-full text-xs flex items-center justify-center border transition-all ${form.gender === g
                        ? 'bg-black text-white border-black'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                        }`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
                {showErr('gender') && <p className="mt-1 text-[10px] text-red-500">{errors.gender}</p>}
              </div>
            </div>

            {/* Password */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                id="password"
                label="Password"
                type="password"
                value={form.password}
                onChange={onChange('password')}
                onBlur={onBlur('password')}
                error={showErr('password')}
              />
              <InputField
                id="confirmPassword"
                label="Confirm Password"
                type="password"
                value={form.confirm_password}
                onChange={onChange('confirm_password')}
                onBlur={onBlur('confirm_password')}
                error={showErr('confirm_password')}
              />
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-black text-white py-4 text-xs tracking-[0.2em] uppercase hover:bg-gray-900 transition-colors disabled:opacity-50"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>

              <div className="mt-6 text-center">
                <p className="text-xs text-gray-500">
                  Already have an account?{' '}
                  <Link to="/login" className="text-black font-medium underline underline-offset-4">
                    Sign In
                  </Link>
                </p>
              </div>
            </div>

          </form>
        </div>
      </div>

      {/* OTP Modal */}
      <AnimatePresence>
        {otpOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-sm bg-white p-8 shadow-2xl"
            >
              <h3 className="text-xl font-serif text-center mb-2">Verify Phone</h3>
              <p className="text-xs text-center text-gray-500 mb-6">
                Enter the code sent to <span className="font-bold text-black">{form.phone_number}</span>
              </p>

              <div className="flex justify-center gap-2 mb-6">
                {otpDigits.map((d, i) => (
                  <input
                    key={i}
                    type="tel"
                    maxLength={1}
                    className="w-10 h-12 border-b-2 border-gray-200 text-center text-xl focus:border-black focus:outline-none transition-colors"
                    value={d}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '').slice(0, 1);
                      setOtpDigits(arr => { const n = [...arr]; n[i] = v; return n; });
                      if (v && i < 5) e.target.parentElement.querySelectorAll('input')[i + 1]?.focus();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' && !otpDigits[i] && i > 0) {
                        e.currentTarget.parentElement.querySelectorAll('input')[i - 1]?.focus();
                      }
                    }}
                  />
                ))}
              </div>

              {otpNotice.text && (
                <p className={`text-center text-xs mb-4 ${otpNotice.type === 'error' ? 'text-red-500' : 'text-green-500'}`}>
                  {otpNotice.text}
                </p>
              )}

              <div className="flex justify-between items-center mb-6 text-[10px] text-gray-400 uppercase tracking-wider">
                <span>{formatMMSS(otpSecondsLeft)} remaining</span>
                <button onClick={sendOtp} disabled={otpLoading} className="hover:text-black underline">Resend</button>
              </div>

              <div className="space-y-3">
                <button
                  onClick={async () => {
                    setOtpNotice({ type: '', text: '' });
                    try { await verifyOtp(); } catch { }
                  }}
                  disabled={otpLoading}
                  className="w-full bg-black text-white py-3 text-xs tracking-[0.2em] uppercase hover:bg-gray-900"
                >
                  {otpLoading ? 'Verifying...' : 'Verify'}
                </button>
                <button
                  onClick={() => setOtpOpen(false)}
                  className="w-full border border-gray-200 py-3 text-xs tracking-[0.2em] uppercase hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}